import { _ } from "../core/polyglot/polyglot.js";
import { defaultPeriodTypes } from "../accounting/shared/PeriodTypePicker/PeriodTypePicker.js";
import { isSameDay } from "../core/helpers/date-util.js";
import {
	makeActualsSubColumnSpec,
	makeBudgetSubColumnSpec,
	makeChangeColumnTemplate,
	makeForecastEstimateSubColumnSpec,
	makeFutureRepeatingColumnTemplate,
	makeInitialReportTemplate,
	makePastRepeatingColumnTemplate,
	makeSummaryColumnTemplate,
	makeYearToDateColumnTemplate,
	makeYearToDateGroupColumnTemplate,
} from "../accounting/comparisonTool/document/reportItemBuilders.js";
import deviationTypeFactory from "./deviationTypes/deviationTypeFactory.js";
import ManualGroupColumnTemplate from "./reports/templates/columnTemplates/groups/ManualGroupColumnTemplate.js";
import SeparatorColumnTemplate from "./reports/templates/columnTemplates/SeparatorColumnTemplate.js";

/**
 *	This class manages the {@link ReportTemplate} for the {@link ComparisonToolDocumentState}
 *	It is responsible to holds {@link SubColumnSpec} and transforms them to
 *	{@link AbstractColumnTemplate}.
 *
 * @param {Period} period - global period for the Report.
 * @param {Forecast[]} forecastEstimates -
 * @param {AbstractRowTemplate[]} rowTemplates - the default row templates of the
 *   report template.
 * @param {string} reportId - unique identifier of the original report
 *   the rows are coming from (e.g, "income-statement")
 */
export default class ComparisonReportTemplate {
	constructor({ period, forecastEstimates, rowTemplates, reportId }) {
		this._period = period;
		this._reportId = reportId;

		this._resetSummarySpecs();

		this._idToForecastEstimates = {};
		for (const forecastEstimate of forecastEstimates) {
			this._idToForecastEstimates[forecastEstimate.getId()] =
				forecastEstimate;
		}

		this._pastRepeatingColumnTemplate = makePastRepeatingColumnTemplate();

		this._futureRepeatingColumnTemplate =
			makeFutureRepeatingColumnTemplate();

		this._yearToDateGroupColumnTemplate = makeYearToDateGroupColumnTemplate(
			{ period: this._period },
		);

		this._summaryColumnTemplate = new ManualGroupColumnTemplate();

		this._initSubColumnSpecs();

		this._reportTemplate = makeInitialReportTemplate({
			rowTemplates,
			columnTemplates: [
				this._pastRepeatingColumnTemplate,
				this._futureRepeatingColumnTemplate,
				this._yearToDateGroupColumnTemplate,
				this._summaryColumnTemplate,
			],
		});
	}

	setReportTemplate({ id, rows }) {
		this._reportId = id;
		this._reportTemplate.setRows(rows);
	}

	hasReportId(id) {
		return this._reportId === id;
	}

	getForecastEstimateById(forecastEstimateId) {
		return this._idToForecastEstimates[forecastEstimateId];
	}

	getPastRepeatingColumnTemplate() {
		return this._pastRepeatingColumnTemplate;
	}

	getFutureRepeatingColumnTemplate() {
		return this._futureRepeatingColumnTemplate;
	}

	getYearToDateGroupColumnTemplate() {
		return this._yearToDateGroupColumnTemplate;
	}

	setPeriodType(periodType) {
		this._period = this._period.asPeriodType(periodType);
		this._pastRepeatingColumnTemplate.setPeriodType(periodType);
		this._futureRepeatingColumnTemplate.setPeriodType(periodType);
	}

	changePeriod(period) {
		this._period = period;
		this._initSubColumnSpecs();
		this._changePeriodOfYearToDateGroupColumnTemplate();
		return this.updateReportTemplate();
	}

	updateReportTemplate() {
		this._pastRepeatingColumnTemplate.setColumnTemplate(
			this._interpretSpecsToManualGroup(),
		);

		this._futureRepeatingColumnTemplate.setColumnTemplate(
			this._interpretSpecsToManualGroup({
				isForFuturePeriods: true,
			}),
		);

		this._yearToDateGroupColumnTemplate.setColumnTemplates(
			this._interpretSubColumnSpecsToColumnTemplates({
				specs: this._subColumnSpecs,
				columnTemplateMakeFunction: makeYearToDateColumnTemplate,
			}),
		);

		this._summaryColumnTemplate.setColumnTemplates(
			this._interpretSummaryColumns(),
		);

		return this._reportTemplate;
	}

	getSubColumnSpecs() {
		return this._subColumnSpecs;
	}

	setSubColumnSpecs(specs) {
		this._subColumnSpecs = specs;
	}

	getSummarySpecs({ periodType }) {
		return this._summarySpecsByPeriodType.get(periodType);
	}

	setSummarySpecs({ specs, periodType }) {
		this._summarySpecsByPeriodType.set(periodType, specs);
	}

	/**
	 * Returns the default period types that have a bigger timespan
	 * than the report template's period (e.g., if `this._period` is
	 * month, it will return [quarter, halfYear, year]).
	 */
	getLargerPeriodTypes() {
		const index = this._getDefaultPeriodTypeIndex(
			this._period.getPeriodType(),
		);

		return defaultPeriodTypes.slice(index + 1);
	}

	_getDefaultPeriodTypeIndex(periodTypeId) {
		return defaultPeriodTypes.findIndex(
			(periodType) => periodType.getId() === periodTypeId,
		);
	}

	_changePeriodOfYearToDateGroupColumnTemplate() {
		this._yearToDateGroupColumnTemplate = makeYearToDateGroupColumnTemplate(
			{
				period: this._period,
			},
		);

		this._reportTemplate?.setColumns([
			this.getPastRepeatingColumnTemplate(),
			this.getFutureRepeatingColumnTemplate(),
			this.getYearToDateGroupColumnTemplate(),
		]);
	}

	_initSubColumnSpecs() {
		this._subColumnSpecs = [makeActualsSubColumnSpec()];

		const defaultSubColumnSpec =
			this._makeDefaultForecastEstimateSubColumnSpec() ||
			this._makeDefaultBudgetSubColumnSpec();

		if (defaultSubColumnSpec) {
			this._subColumnSpecs.push(defaultSubColumnSpec);
		}
	}

	_interpretSpecsToManualGroup({ isForFuturePeriods = false } = {}) {
		return new ManualGroupColumnTemplate({
			columnTemplates: [
				...this._interpretSubColumnSpecsToColumnTemplates({
					specs: this._subColumnSpecs,
					columnTemplateMakeFunction: makeChangeColumnTemplate,
					isForFuturePeriods,
				}),
				new SeparatorColumnTemplate({
					borderOnly: true,
				}),
			],
		});
	}

	_interpretSubColumnSpecsToColumnTemplates({
		specs,
		isForFuturePeriods = false,
		columnTemplateMakeFunction,
	}) {
		if (!specs.length) return undefined;

		const subColumnTemplates = [];
		let actualsColumnTemplate = undefined;

		specs.forEach((spec) => {
			if (spec.isActualsSpec() && isForFuturePeriods) {
				return;
			}

			if (spec.isActualsSpec()) {
				actualsColumnTemplate = columnTemplateMakeFunction({
					title: spec.getTitle(),
					source: spec.getSource(),
					reportTemplate: this._reportTemplate,
				});

				subColumnTemplates.push(actualsColumnTemplate);
				return;
			}

			subColumnTemplates.push(
				...this._interpretSubColumnSpecToColumnTemplates({
					spec,
					columnTemplateMakeFunction,
					actualsColumnTemplate,
					isForFuturePeriods,
				}),
			);
		});

		return subColumnTemplates;
	}

	_resetSummarySpecs() {
		this._summarySpecsByPeriodType = new Map();

		defaultPeriodTypes.forEach((periodType) =>
			this.setSummarySpecs({ specs: [], periodType }),
		);
	}

	_interpretSummaryColumns() {
		const summaryGroupColumnTemplates = [];

		for (const [periodType, specs] of this._summarySpecsByPeriodType) {
			if (!this._isPeriodTypeVisible(periodType)) continue;

			summaryGroupColumnTemplates.push(
				new SeparatorColumnTemplate({ borderOnly: true }),
				this._interpretSummarySpecs({ specs, periodType }),
			);
		}

		return summaryGroupColumnTemplates;
	}

	_isPeriodTypeVisible(periodType) {
		return (
			!!this.getSummarySpecs({ periodType }).length &&
			this._isPeriodTypeLargerThanCurrent(periodType)
		);
	}

	_isPeriodTypeLargerThanCurrent(periodType) {
		return (
			this._getDefaultPeriodTypeIndex(periodType.getId()) >
			this._getDefaultPeriodTypeIndex(this._period.getPeriodType())
		);
	}

	_interpretSummarySpecs({ specs, periodType }) {
		const columnTemplates = specs.map((subColumnSpec) =>
			makeSummaryColumnTemplate({
				subColumnSpec,
				reportTemplate: this._reportTemplate,
				periodType,
			}),
		);

		return new ManualGroupColumnTemplate({
			title: _("Summary for {periodType}", {
				periodType: periodType.getName(),
			}),
			columnTemplates,
		});
	}

	_interpretSubColumnSpecToColumnTemplates({
		spec,
		columnTemplateMakeFunction,
		actualsColumnTemplate,
		isForFuturePeriods = false,
	}) {
		const columnTemplate = columnTemplateMakeFunction({
			title: spec.getTitle(),
			source: spec.getSource(),
			reportTemplate: this._reportTemplate,
		});

		if (isForFuturePeriods) {
			return [columnTemplate];
		}

		const deviationColumnTemplates = deviationTypeFactory
			.makeAll()
			.filter((deviationType) => spec.hasDeviationType(deviationType))
			.map((deviationType) =>
				deviationType.makeDeviationColumnTemplate({
					baseValuesColumnTemplateId: actualsColumnTemplate.getId(),
					deviatedValuesColumnTemplateId: columnTemplate.getId(),
					reportTemplate: this._reportTemplate,
				}),
			);

		return [columnTemplate, ...deviationColumnTemplates];
	}

	_makeDefaultForecastEstimateSubColumnSpec() {
		const forecastEstimateCandidate = Object.values(
			this._idToForecastEstimates,
		).find((forecastEstimate) =>
			forecastEstimate
				.getPredictedPeriods()
				.some((predictedPeriod) =>
					isSameDay(
						predictedPeriod.asDate(),
						this._period.getStart(),
					),
				),
		);

		if (!forecastEstimateCandidate) {
			return undefined;
		}

		return makeForecastEstimateSubColumnSpec(forecastEstimateCandidate);
	}

	_makeDefaultBudgetSubColumnSpec() {
		const budget = this._period.getYear().getBudgets()[0];

		if (!budget) {
			return undefined;
		}

		return makeBudgetSubColumnSpec(budget);
	}
}
